import React, { useState } from 'react';
import { useTemplate } from '../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../utils/Button';
import FocusControl from '../../../utils/FocusControl';
import { handleUpdateCustomerBillTo } from '../CustomerDetails/CustomerDetailsOperation';

/**
 * EditBillToModal Component
 *
 * This modal allows users to change the billing entity for a customer.
 * It provides two options:
 * 1. Bill to Customer - Bills will be sent directly to the customer
 * 2. Bill to Agency - Bills will be sent to the customer's agency
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Customer data object containing customerId, agencyId, current billing info
 * @param {Function} props.handleReload - Function to reload customer data after successful update
 * @param {Function} props.closeModal - Function to close the modal
 */
function EditBillToModal({ data, handleReload, closeModal }) {
    const { setMessage, setConfirm } = useTemplate();
    const [isError, setIsError] = useState('');
    const [loading, setLoading] = useState(false);

    // Initialize with current billing type or default to 'customer'
    // API returns isBillToAgency (boolean), convert to 'agency' or 'customer'
    const [selectedBillTo, setSelectedBillTo] = useState(
        data?.isBillToAgency ? 'agency' : 'customer'
    );

    const handleBillToChange = (value) => {
        setSelectedBillTo(value);
        setIsError(''); // Clear any previous errors
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Prepare the data for the API call
            const requestData = {
                billTo: selectedBillTo,
                customerId: data.id,
                agencyId: data.agencyId
            };

            // Call the API to update the bill to setting
            const response = await handleUpdateCustomerBillTo(data.id, requestData);

            // On success
            setMessage({
                type: 'success',
                text:
                    response.title ||
                    `Bill To updated successfully to ${selectedBillTo === 'customer' ? 'Customer' : 'Agency'}`
            });

            // Reload data and close modal
            if (handleReload) {
                handleReload();
            }

            if (closeModal) {
                closeModal();
            } else {
                setConfirm(null);
            }
        } catch (error) {
            const errorMessage =
                error?.message || error?.toString() || 'Failed to update Bill To setting';
            setMessage({
                type: 'error',
                text: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (closeModal) {
            closeModal();
        } else {
            setConfirm(null);
        }
    };

    FocusControl(isError, setIsError, selectedBillTo);

    return (
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4 p-4">
            <div className="mb-2">
                <p className="text-base font-normal text-gray-700 mb-4">
                    Select who should receive the billing information for this customer:
                </p>
            </div>

            {/* Bill To Customer Option */}
            <div
                className={`
                    border-2 rounded-lg p-4 cursor-pointer transition-all duration-200
                    ${
                        selectedBillTo === 'customer'
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                    }
                `}
                role="button"
                tabIndex={0}
                onClick={() => handleBillToChange('customer')}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleBillToChange('customer');
                    }
                }}
            >
                <label className="flex items-start cursor-pointer">
                    <div className="flex items-center h-6">
                        <input
                            type="radio"
                            name="billTo"
                            value="customer"
                            checked={selectedBillTo === 'customer'}
                            onChange={(e) => handleBillToChange(e.target.value)}
                            className="w-5 h-5 text-primary focus:ring-primary cursor-pointer"
                        />
                    </div>
                    <div className="ml-3 flex-1">
                        <div className="text-base font-semibold text-gray-900">
                            Bill to Customer
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                            All invoices and billing communications will be sent directly to the
                            customer.
                            {data?.organization && (
                                <span className="block mt-1 font-medium text-primary">
                                    {data.organization}
                                </span>
                            )}
                        </div>
                    </div>
                </label>
            </div>

            {/* Bill To Agency Option */}
            <div
                className={`
                    border-2 rounded-lg p-4 cursor-pointer transition-all duration-200
                    ${
                        selectedBillTo === 'agency'
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                    }
                `}
                role="button"
                tabIndex={0}
                onClick={() => handleBillToChange('agency')}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleBillToChange('agency');
                    }
                }}
            >
                <label className="flex items-start cursor-pointer">
                    <div className="flex items-center h-6">
                        <input
                            type="radio"
                            name="billTo"
                            value="agency"
                            checked={selectedBillTo === 'agency'}
                            onChange={(e) => handleBillToChange(e.target.value)}
                            className="w-5 h-5 text-primary focus:ring-primary cursor-pointer"
                        />
                    </div>
                    <div className="ml-3 flex-1">
                        <div className="text-base font-semibold text-gray-900">Bill to Agency</div>
                        <div className="text-sm text-gray-600 mt-1">
                            All invoices and billing communications will be sent to the
                            customer&apos;s agency.
                            {data?.agencyInfo?.organization && (
                                <span className="block mt-1 font-medium text-primary">
                                    {data.agencyInfo.organization}
                                </span>
                            )}
                        </div>
                    </div>
                </label>
            </div>

            {/* Error Message */}
            {isError && <p className="text-error text-sm text-left">{isError}</p>}

            {/* Action Buttons */}
            <div className="w-full flex justify-end items-center gap-2 mt-4 pt-4 border-t border-gray-200">
                <ButtonCancel title="Cancel" onClick={handleCancel} disabled={loading} />
                <ButtonPrimary
                    title="Save Changes"
                    btnType="submit"
                    loading={loading}
                    disabled={loading}
                />
            </div>
        </form>
    );
}

export default EditBillToModal;
